import React, { useEffect, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { LogIn, Github } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { readLastLoginEmail, saveLastLoginEmail } from '../../lib/loginEmailStorage';
import { isGithubLoginConfigured } from '../../lib/githubAuthAllowlist';
import { LOW_CODE_LOGO_DARK_URL, LOW_CODE_LOGO_URL } from '../../lib/siteBranding';

const AuthForm: React.FC = () => {
  const [email, setEmail] = useState(() => readLastLoginEmail());
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { signIn, signInWithGitHub } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const saved = readLastLoginEmail();
    if (saved) {
      setEmail(saved);
      passwordRef.current?.focus();
    } else {
      emailRef.current?.focus();
    }
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    const normalizedEmail = email.trim().toLowerCase();

    try {
      await signIn(normalizedEmail, password);
      saveLastLoginEmail(normalizedEmail);
      navigate('/');
    } catch (error: any) {
      let errorMessage = 'Invalid email or password. Please try again.';
      if (error?.message) {
        errorMessage = error.message;
      }
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGitHubSignIn = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGitHub();
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'GitHub sign in failed');
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
          <div className="p-8">
            <div className="text-center mb-8">
              <img
                src={LOW_CODE_LOGO_URL}
                alt=""
                width={140}
                height={32}
                className="mx-auto mb-8 h-20 w-auto dark:hidden sm:h-24"
                decoding="async"
              />
              <img
                src={LOW_CODE_LOGO_DARK_URL}
                alt=""
                width={140}
                height={32}
                className="mx-auto mb-8 hidden h-20 w-auto dark:block sm:h-24"
                decoding="async"
              />
            </div>
            
            {error && (
              <div className="mb-6 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md">
                <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              {isGithubLoginConfigured() ? (
                <>
                  <button
                    type="button"
                    disabled={isLoading}
                    onClick={() => void handleGitHubSignIn()}
                    className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 py-2 px-4 text-gray-900 dark:text-white transition-colors hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 dark:focus:ring-neutral-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <Github className="w-5 h-5 mr-2" />
                    Continue with GitHub
                  </button>

                  <div className="relative py-1">
                    <div className="absolute inset-0 flex items-center" aria-hidden="true">
                      <div className="w-full border-t border-gray-200 dark:border-gray-600" />
                    </div>
                    <div className="relative flex justify-center text-xs uppercase">
                      <span className="bg-white dark:bg-gray-800 px-2 text-gray-500 dark:text-gray-400">or</span>
                    </div>
                  </div>
                </>
              ) : null}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <div className="relative">
                  <input
                    ref={emailRef}
                    id="email"
                    name="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="username"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="your@email.com"
                  />
                </div>
              </div>
              
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    ref={passwordRef}
                    id="password"
                    name="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="••••••••"
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={isLoading}
                className="w-full rounded-md bg-black py-2 px-4 text-white transition-colors hover:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-neutral-900 focus:ring-offset-2 dark:focus:ring-neutral-100 flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? (
                  <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Sign In
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AuthForm;